import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Cog, Wrench, ClipboardList, Zap, Shield, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getOptimizedUrl, SERVICE_IMAGE_MAP } from '@/generated-images-config';

const Services = () => {
  const navigate = useNavigate();
  const services = [
    {
      icon: Cog,
      title: 'Mantenimiento Industrial',
      description: 'Servicio completo de mantenimiento preventivo y correctivo para maquinaria industrial de todo tipo.',
      features: ['Mantenimiento preventivo', 'Reparaciones de emergencia', 'Diagnóstico avanzado', 'Programación personalizada'],
      color: 'bg-blue-500',
      imageId: SERVICE_IMAGE_MAP.maintenance
    },
    {
      icon: Wrench,
      title: 'Instalación y Montaje',
      description: 'Instalación profesional de equipos industriales con garantía de cumplimiento de normativas.',
      features: ['Montaje de maquinaria', 'Puesta en marcha', 'Calibración de equipos', 'Capacitación técnica'],
      color: 'bg-green-500',
      imageId: SERVICE_IMAGE_MAP.installation
    },
    {
      icon: ClipboardList,
      title: 'Consultoría Técnica',
      description: 'Asesoría especializada para optimización de procesos y selección de equipos.',
      features: ['Auditoría de procesos', 'Ingeniería de proyectos', 'Selección de equipos', 'Optimización de producción'],
      color: 'bg-purple-500',
      imageId: SERVICE_IMAGE_MAP.consulting
    },
    {
      icon: Zap,
      title: 'Repuestos y Accesorios',
      description: 'Amplio stock de repuestos originales y compatibles para principal marcas industriales.',
      features: ['Repuestos originales', 'Entrega rápida', 'Garantía de calidad', 'Alternativas económicas'],
      color: 'bg-orange-500',
      imageId: SERVICE_IMAGE_MAP.parts
    },
    {
      icon: Shield,
      title: 'Inspección y Certificación',
      description: 'Evaluaciones técnicas y certificaciones para cumplimiento de estándares industriales.',
      features: ['Inspecciones de seguridad', 'Certificación de equipos', 'Cumplimiento normativo', 'Documentación técnica'],
      color: 'bg-red-500',
      imageId: SERVICE_IMAGE_MAP.inspection
    },
    {
      icon: Users,
      title: 'Capacitación Técnica',
      description: 'Programas de formación para operadores y técnicos de mantenimiento industrial.',
      features: ['Cursos operativos', 'Capacitación en seguridad', 'Actualización técnica', 'Certificación de personal'],
      color: 'bg-cyan-500',
      imageId: SERVICE_IMAGE_MAP.training
    }
  ];

  const handleServiceInfo = (serviceName: string) => {
    const serviceOptions = {
      'Mantenimiento Industrial': 'mantenimiento',
      'Instalación y Montaje': 'instalacion',
      'Consultoría Técnica': 'consultoria',
      'Repuestos y Accesorios': 'repuestos',
      'Inspección y Certificación': 'inspeccion',
      'Capacitación Técnica': 'capacitacion'
    };
    
    const serviceValue = serviceOptions[serviceName as keyof typeof serviceOptions];
    
    // Store the selected service in sessionStorage to be used on the contact page
    if (serviceValue) {
      sessionStorage.setItem('selectedService', serviceValue);
    }
    
    // Navigate to contact page using React Router
    navigate('/contact');
  };

  const handleConsultingRequest = () => {
    // Store the consulting service in sessionStorage
    sessionStorage.setItem('selectedService', 'consultoria');
    
    // Navigate to contact page using React Router
    navigate('/contact');
  };

  const handleDownloadBrochure = () => {
    // Create a simple brochure download
    const brochureContent = `
      ATOMIC CORPORATION - CATÁLOGO DE SERVICIOS
      
      NUESTROS SERVICIOS:
      
      1. MANTENIMIENTO INDUSTRIAL
         - Mantenimiento preventivo
         - Reparaciones de emergencia
         - Diagnóstico avanzado
         - Programación personalizada
      
      2. INSTALACIÓN Y MONTAJE
         - Montaje de maquinaria
         - Puesta en marcha
         - Calibración de equipos
         - Capacitación técnica
      
      3. CONSULTORÍA TÉCNICA
         - Auditoría de procesos
         - Ingeniería de proyectos
         - Selección de equipos
         - Optimización de producción
      
      4. REPUESTOS Y ACCESORIOS
         - Repuestos originales
         - Entrega rápida
         - Garantía de calidad
         - Alternativas económicas
      
      5. INSPECCIÓN Y CERTIFICACIÓN
         - Inspecciones de seguridad
         - Certificación de equipos
         - Cumplimiento normativo
         - Documentación técnica
      
      6. CAPACITACIÓN TÉCNICA
         - Cursos operativos
         - Capacitación en seguridad
         - Actualización técnica
         - Certificación de personal
      
      CONTACTO:
      Teléfono: +51 953 759 419
      Email: ventas@atomcorporations.com
      Dirección: Av. Industrial 1234, Lima, Perú
    `;
    
    const blob = new Blob([brochureContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Atomic-Corporation-Servicios.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <section id="services" className="section-padding bg-gray-50">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-6">
            Nuestros Servicios
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Soluciones
            <span className="text-gradient"> Complejas</span>
            para la Industria Moderna
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Ofrecemos un portafolio integral de servicios diseñados para maximizar 
            la eficiencia y confiabilidad de sus operaciones industriales.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            const imageUrl = getOptimizedUrl(service.imageId, 400, 300);
            return (
              <Card key={index} className="card-hover group cursor-pointer border-0 shadow-lg hover:shadow-2xl overflow-hidden">
                {/* Image Header */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={imageUrl || ''}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className={`absolute bottom-4 left-4 w-12 h-12 ${service.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full group-hover:bg-primary-600 group-hover:text-white group-hover:border-primary-600 transition-all duration-300" onClick={() => handleServiceInfo(service.title)}>
                    Más Información
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-primary-600 to-accent-500 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            ¿Necesita una solución personalizada?
          </h3>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Nuestro equipo de expertos está listo para analizar sus necesidades 
            y desarrollar soluciones a medida para su industria.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 text-lg" onClick={handleConsultingRequest}>
              Solicitar Asesoría
            </Button>
            <Button variant="outline" className="border-white text-white bg-transparent hover:bg-white hover:text-primary-600 px-8 py-4 text-lg" onClick={handleDownloadBrochure}>
              Descargar Brochure
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
